import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MailX, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const recno = searchParams.get('recno') || '';

  const [status, setStatus] = useState('loading'); // loading, confirm, success, error, already
  const [maskedEmail, setMaskedEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!token || !recno) {
      setStatus('error');
      setErrorMsg('Invalid unsubscribe link.');
      return;
    }

    axios.get(`${API_BASE}/unsubscribe/`, { params: { token, recno } })
      .then(({ data }) => {
        if (data.valid) {
          setMaskedEmail(data.masked_email);
          setStatus('confirm');
        } else {
          setStatus('error');
          setErrorMsg('Invalid unsubscribe link.');
        }
      })
      .catch(err => {
        setStatus('error');
        setErrorMsg(err.response?.data?.error || 'Invalid or expired unsubscribe link.');
      });
  }, [token, recno]);

  const handleUnsubscribe = async () => {
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API_BASE}/unsubscribe/`, { token, recno });
      if (data.message?.includes('already')) {
        setStatus('already');
      } else {
        setStatus('success');
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setErrorMsg('Too many requests. Please try again later.');
      } else {
        setErrorMsg(err.response?.data?.error || 'Something went wrong.');
      }
      setStatus('error');
    }
    setProcessing(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f5f6fa',
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      padding: 20,
    }}>
      <div style={{
        background: 'white',
        borderRadius: 16,
        padding: '48px 40px',
        maxWidth: 480,
        width: '100%',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        textAlign: 'center',
      }}>

        {status === 'loading' && (
          <>
            <Loader size={48} style={{ color: '#4f7df5', animation: 'spin 1s linear infinite', marginBottom: 20 }} />
            <p style={{ color: '#5a6070', fontSize: '1rem' }}>Verifying your unsubscribe link...</p>
          </>
        )}

        {status === 'confirm' && (
          <>
            <MailX size={48} style={{ color: '#4f7df5', marginBottom: 20 }} />
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1d2d', marginBottom: 12 }}>Unsubscribe</h1>
            <p style={{ color: '#5a6070', fontSize: '1rem', marginBottom: 8 }}>
              Are you sure you want to unsubscribe?
            </p>
            <p style={{ color: '#8b90a0', fontSize: '.9rem', marginBottom: 32 }}>
              Email: <strong style={{ color: '#1a1d2d' }}>{maskedEmail}</strong>
            </p>
            <p style={{ color: '#8b90a0', fontSize: '.82rem', marginBottom: 32 }}>
              You will no longer receive recruitment emails from us.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                onClick={() => window.close()}
                style={{
                  padding: '12px 28px', borderRadius: 10, border: '1px solid #d8dce6',
                  background: 'white', color: '#5a6070', fontSize: '.9rem', fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleUnsubscribe}
                disabled={processing}
                style={{
                  padding: '12px 28px', borderRadius: 10, border: 'none',
                  background: '#dc2626', color: 'white', fontSize: '.9rem', fontWeight: 600,
                  cursor: processing ? 'not-allowed' : 'pointer', opacity: processing ? 0.6 : 1,
                  fontFamily: 'inherit',
                }}
              >
                {processing ? 'Processing...' : 'Yes, Unsubscribe'}
              </button>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle size={48} style={{ color: '#16a34a', marginBottom: 20 }} />
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1d2d', marginBottom: 12 }}>Unsubscribed</h1>
            <p style={{ color: '#5a6070', fontSize: '1rem' }}>
              You have been successfully unsubscribed. You will no longer receive recruitment emails from us.
            </p>
          </>
        )}

        {status === 'already' && (
          <>
            <CheckCircle size={48} style={{ color: '#4f7df5', marginBottom: 20 }} />
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1d2d', marginBottom: 12 }}>Already Unsubscribed</h1>
            <p style={{ color: '#5a6070', fontSize: '1rem' }}>
              This email address has already been unsubscribed.
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle size={48} style={{ color: '#dc2626', marginBottom: 20 }} />
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1d2d', marginBottom: 12 }}>Error</h1>
            <p style={{ color: '#5a6070', fontSize: '1rem' }}>{errorMsg}</p>
          </>
        )}
      </div>
    </div>
  );
}